<template>
  <div class="blog-page">
    <template v-if="getCurrentPage == 1">
      <!-- Search Article -->
      <SearchBar
        class="mt-[30px] mb-[40px] md:mb-[66px]"
        :search-value="getSearchQuery"
        :title="getSearchBarTitle"
        :description="searchResultDescription"
        @search="onSearch"
      />
      <!-- ./Search Article -->

      <!-- Featured Article -->
      <FeaturedArticle
        v-if="!getSearchQuery && postState.featuredArticle"
        :featured-article="postState.featuredArticle"
      />
      <!-- ./Featured Article -->

      <!-- Subscription Section -->
      <SubscriptionBar v-if="!getSearchQuery" background-class="bg-[#f5f5f5]" />
      <!-- ./Subscription Section -->
    </template>

    <!-- Categories -->
    <CategoryList v-if="!getSearchQuery" />
    <!-- ./Categories -->

    <!-- Post List -->
    <PostList :posts="postState.list.posts" />
    <!-- ./Post List -->

    <!-- Pagination -->
    <Pagination
      v-if="postState.list.totalPage > 1"
      :current-page="getCurrentPage"
      :total-page="postState.list.totalPage"
      :route-path="getRoutePath"
    />
    <!-- ./Pagination -->
  </div>
</template>
<script>
import SearchBar from '~/components/blog/SearchBar.vue';
import FeaturedArticle from '~/components/blog/FeaturedArticle.vue';
import SubscriptionBar from '~/components/blog/subscriptionBar/Index.vue';
import CategoryList from '~/components/blog/categories/CategoryList.vue';
import PostList from '~/components/blog/PostList.vue';

const Pagination = () => import('~/components/blog/Pagination.vue');

export default {
  name: 'Blog',
  components: {
    SearchBar,
    FeaturedArticle,
    SubscriptionBar,
    CategoryList,
    PostList,
    Pagination,
  },
  layout: 'blog',
  head() {
    const link = this.getHeadLinks;

    return {
      title: this.meta.title || 'Parkos - Blog',
      htmlAttrs: {
        lang: this.getHrefLang,
      },
      meta: [
        { 'http-equiv': 'content-language', content: this.getHrefLang },
        {
          property: 'og:title',
          content: this.meta.title,
        },
        {
          name: 'description',
          content: this.meta.description,
        },
        {
          property: 'og:description',
          content: this.meta.description,
        },
        { name: 'twitter:card', content: 'summary' },
        {
          name: 'twitter:title',
          content: this.meta.title,
        },
        {
          name: 'twitter:site',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:creator',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:description',
          content: this.meta.description,
        },
        // {
        //   name: 'twitter:image',
        //   content: thumbnail,
        // },
        { property: 'og:type', content: 'article' },
        { property: 'og:locale', content: this.getHrefLang },
        // {
        //   property: 'og:image',
        //   content: thumbnail,
        // },
        {
          property: 'og:url',
          content: `${this.$paths.url(false)}${
            this.$route.path[this.$route.path.length - 1] === '/'
              ? `${this.$route.path.substring(0, this.$route.path.length - 1)}/`
              : `${this.$route.path}/`
          }`,
        },
      ],
      link,
    };
  },
  jsonld() {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': `${this.$paths.url(false)}/blog/`,
            name: 'Blog',
          },
        },
      ],
    };
  },
  computed: {
    language() {
      return this.$store.state.language;
    },
    languages() {
      return this.$store.state.languages;
    },
    postState() {
      return this.$store.state.post;
    },
    getCurrentPage() {
      return parseInt(this.$route?.query?.page || 1, 10);
    },
    getRoutePath() {
      return this.$router.resolve({
        name: 'blog',
      }).href;
    },
    getHeadLinks() {
      const links = [];

      let canonical = this.$paths.url(false);

      if (this.getCurrentPage > 1) {
        canonical += this.$router.resolve({
          name: 'blog',
        }).href;
        canonical += `/?page=${this.getCurrentPage}`;
      } else {
        canonical += this.$route.path;
      }

      links.push({
        rel: 'canonical',
        href: canonical,
      });

      const previousPage = this.getCurrentPage - 1;
      if (previousPage > 0) {
        const prevPageResolver = {
          name: 'blog',
        };

        let prevPageLink = `${this.$router.resolve(prevPageResolver).href}/`;

        if (previousPage !== 1) {
          prevPageLink += `?page=${previousPage}`;
        }

        links.push({
          rel: 'prev',
          href: this.$paths.url(false) + prevPageLink,
        });
      }

      const nextPage = this.getCurrentPage + 1;
      if (nextPage <= this.postState.list.totalPage) {
        let nextPageLink = this.$router.resolve({
          name: 'blog',
        }).href;
        nextPageLink += `/?page=${nextPage}`;

        links.push({
          rel: 'next',
          href: this.$paths.url(false) + nextPageLink,
        });
      }

      const alternates = this.languages
        .filter((l) => !l.is_hidden)
        .map((m) => ({
          rel: 'alternate',
          hreflang: m.lang === 'en-eu' ? 'en' : m.lang,
          href: `${(!m.domain.includes('https://') ? 'https://' : '') + m.domain}/blog/`,
        }));
      links.push(...alternates);

      return links;
    },
    getSearchQuery() {
      return this.$route?.query?.q;
    },
    getSearchBarTitle() {
      let title = this.$i18n('blog.our-blog-title');

      if (this.getSearchQuery)
        title = this.$i18n('blog.search-results-for', {
          category: this.getSearchQuery,
        });

      return title;
    },
    searchResultDescription() {
      let message = this.$i18n('blog.our-blog-description');

      if (this.getSearchQuery) {
        const postCount = this.postState.list.posts.length;

        if (postCount) {
          message = this.$i18n('blog.posts-found', { count: postCount });
        } else {
          message = this.$i18n('blog.no-result-for-search');
        }
      }

      return message;
    },
    meta() {
      const title = this.$store.state.page.blogPage?.[0]?.meta.jetpack_seo_html_title || '';
      const description = this.$store.state.page.blogPage?.[0]?.meta.advanced_seo_description || '';

      return {
        title,
        description,
      };
    },
    getHrefLang() {
      return this.language.lang === 'en-eu' ? 'en' : this.language.lang;
    },
  },
  methods: {
    onSearch(text) {
      const blogRoute = {
        name: 'blog',
      };

      if (text && text.length) {
        blogRoute.query = {
          q: text,
        };
      }

      const route = this.$router.resolve(blogRoute).href;

      window.location = route;
    },
  },
};
</script>
